import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Store, Mail, Phone, MapPin, Wallet } from 'lucide-react';
import WalletModal from '../../components/wallet/WalletModal';
import DeleteClientModal from '../../components/marketplace/DeleteClientModal';
import type { User } from '../../types';

interface Order {
  id: string;
  orderNumber: string;
  source: string;
  sourceOrderId: string;
  status: string;
  companyId: string;
  buyerId: string;
  totalAmount: number;
  createdAt: string;
}

interface ExtendedUser extends Omit<User, 'approvalStatus'> {
  walletBalance?: number;
  company?: {
    name: string;
    logoBase64?: string;
  };
  approvalStatus: "pending" | "approved" | "blocked";
}

export default function MarketplaceClientDetails() {
  const [client, setClient] = useState<ExtendedUser | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      loadClientData();
    }
  }, [id]);

  const loadClientData = async () => {
    if (!id) return;

    try {
      // Load user data
      const userDoc = await getDoc(doc(db, 'users', id));
      if (!userDoc.exists()) {
        setError('Cliente não encontrado');
        return;
      }

      const userData = userDoc.data();
      
      // Verify if user is a marketplace client
      if (userData.role !== 'cliente' && userData.role !== 'customer') {
        setError('Utilizador não é um cliente do marketplace');
        return;
      }

      let companyData = null;
      let walletBalance = 0;

      // Load wallet data using user ID
      const walletDoc = await getDoc(doc(db, 'wallets', id));
      if (walletDoc.exists()) {
        walletBalance = walletDoc.data().balance || 0;
      }

      if (userData.companyId) {
        // Load company data
        const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
        if (companyDoc.exists()) {
          companyData = companyDoc.data();
        }
      }

      setClient({
        id: userDoc.id,
        email: userData.email,
        displayName: userData.displayName,
        phone: userData.phone,
        photoBase64: userData.photoBase64,
        role: userData.role,
        companyId: userData.companyId,
        company: companyData ? {
          name: companyData.commercialName || companyData.legalName,
          logoBase64: companyData.logoBase64
        } : undefined,
        walletBalance,
        approvalStatus: userData.approvalStatus || '',
        createdAt: userData.createdAt
      });

      // Load orders
      const ordersQuery = query(
        collection(db, 'orders'),
        where('buyerId', '==', id)
      );
      
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          orderNumber: data.orderNumber || doc.id,
          source: data.source || '',
          sourceOrderId: data.sourceOrderId || '',
          status: data.status || '',
          companyId: data.companyId || '',
          buyerId: data.buyerId || '',
          totalAmount: data.totalAmount || 0,
          createdAt: data.createdAt || ''
        } as Order;
      });

      setOrders(ordersData);
      setError('');
    } catch (err) {
      console.error('Error loading client data:', err);
      setError('Erro ao carregar dados do cliente');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendente';
      case 'processing': return 'Em Processamento';
      case 'shipped': return 'Enviada';
      case 'delivered': return 'Entregue';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center text-red-600">
            {error || 'Cliente não encontrado'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <Store className="text-primary" size={24} />
              <h2 className="text-2xl font-bold text-secondary">
                Detalhes do Cliente
              </h2>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowWalletModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary-dark"
              >
                <Wallet size={20} />
                Gerir Carteira
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Remover Cliente
              </button>
            </div>
          </div>

          {/* Client Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {client.photoBase64 ? (
                    <img
                      src={client.photoBase64}
                      alt={client.displayName || client.email}
                      className="h-16 w-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                      <Store className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">
                    {client.displayName || client.email}
                  </h3>
                  {client.company && (
                    <p className="text-gray-600">{client.company.name}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 text-gray-600">
                <Mail size={20} />
                <span>{client.email}</span>
              </div>

              {client.phone && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone size={20} />
                  <span>{client.phone}</span>
                </div>
              )}

              {client.company?.name && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin size={20} />
                  <span>{client.company.name}</span>
                </div>
              )}
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold mb-4">Carteira</h4>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Saldo Atual</span>
                <span className="text-2xl font-bold text-primary">
                  €{client.walletBalance?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>
          </div>

          {/* Orders */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Histórico de Encomendas</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nº Encomenda
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {order.orderNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        €{order.totalAmount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/dashboard/orders/${order.id}`)}
                          className="text-primary hover:text-primary-dark"
                          title="Ver detalhes"
                        >
                          Ver detalhes
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {orders.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma encomenda encontrada
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showWalletModal && client && (
        <WalletModal
          isOpen={showWalletModal}
          onClose={() => {
            setShowWalletModal(false);
            loadClientData();
          }}
          userId={client.id}
          companyId={client.companyId || ''}
          currentBalance={client.walletBalance || 0}
          userName={client.displayName || client.email}
          userEmail={client.email}
        />
      )}

      {showDeleteModal && (
        <DeleteClientModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          client={client}
          onDeleted={() => navigate('/dashboard/marketplace/clients')}
        />
      )}
    </div>
  );
}